import { useEffect, useMemo, useState, useCallback } from 'react'
import { useLazyQuery } from '@apollo/client'
import useFilters, { useSort } from '../store'
import OPERATION_SEARCH_CONTACT from '../operations-gql/queries'
import { useSites } from 'store'

const LIMIT = 25

const useUsers = () => {
  const [shouldFetchNextPage, setShouldFetchNextPage] = useState(true)
  const [filters] = useFilters()
  const [sort] = useSort()
  const [{ selected: sitesSelected }] = useSites()
  const sfIds = useMemo(
    () => (sitesSelected ? sitesSelected.map(({ sf_id }) => sf_id) : []),
    [sitesSelected],
  )

  const queryVariables = useMemo(() => {
    return {
      site_ids: sfIds,
      limit: LIMIT,
      ...filters,
      sort_by: sort.by,
      sort_direction: sort.direction,
    }
  }, [sfIds, sort.by, sort.direction, filters])

  const [, { error, loading, data, refetch, fetchMore, networkStatus }] =
    useLazyQuery(OPERATION_SEARCH_CONTACT, {
      notifyOnNetworkStatusChange: true,
      variables: queryVariables,
      fetchPolicy: 'network-only'
    })
  
  const users = useMemo(() => (data ? data.contacts : []), [data])
  
  useEffect(() => {
    refetch(queryVariables).then(() => setShouldFetchNextPage(true))
  }, [queryVariables])

  const fetchNextPage = async () => {
    const fetchMoreResult = await fetchMore({
      variables: {
        offset: users.length,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          contacts: [...prev.contacts, ...fetchMoreResult.contacts]
        };
      },
    });
    if (
      !fetchMoreResult.data.contacts.length ||
      fetchMoreResult.data.contacts.length < LIMIT
    ) {
      setShouldFetchNextPage(false);
    }
  };

  const refreshContacts = useCallback(async () => {
    setShouldFetchNextPage(true)
    await refetch(queryVariables)
  }, [refetch, queryVariables])

  return {
    loading: (loading && networkStatus !== 3) || networkStatus === 2,
    loadingNext: networkStatus === 3,
    shouldFetchNextPage:
      !loading && users && users.length >= LIMIT && shouldFetchNextPage,
    error,
    users,
    fetchNextPage,
    refetch: refreshContacts,
  }
}

export default useUsers